import React from "react";
import { Box, Button, Typography } from "@mui/material";
import ProductDevelopmentProcess from "./ProductDevelopmentProcess";

// Get-Started Hero — first-run welcome panel for the Product Studio dashboard.
// Visual design from app/frontend/docs/v1 Phase 2/design_handoff_get_started_hero/
// Stateless / presentational; the parent owns dismissal persistence.

type IconName = "play" | "spark" | "x";

const ICON_PATHS: Record<IconName, string[]> = {
    play: ["M6 4l13 8-13 8z"],
    spark: ["M12 3l1.8 4.7L18 9.5l-4.2 1.8L12 16l-1.8-4.7L6 9.5l4.2-1.8z"],
    x: ["M5 5l10 10", "M15 5L5 15"]
};

interface HeroIconProps {
    name: IconName;
    size?: number;
    strokeWidth?: number;
    color?: string;
}

const HeroIcon: React.FC<HeroIconProps> = ({ name, size = 16, strokeWidth = 1.7, color = "currentColor" }) => {
    const paths = ICON_PATHS[name];
    if (!paths) return null;
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            {paths.map((d, i) => (
                <path key={i} d={d} />
            ))}
        </svg>
    );
};

export interface GetStartedHeroProps {
    /** Primary CTA — start the new-product flow. */
    onStartProduct?: () => void;
    /** Secondary — launch the guided coach-mark tour. */
    onTakeTour?: () => void;
    /** Dismiss — persist so it doesn't reappear. */
    onDismiss?: () => void;
}

export const GetStartedHero: React.FC<GetStartedHeroProps> = ({ onStartProduct, onTakeTour, onDismiss }) => {
    return (
        <Box
            sx={{
                marginBottom: "18px",
                borderRadius: "14px",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                display: "flex",
                position: "relative",
                flexDirection: { xs: "column", md: "row" }
            }}
        >
            {/* LEFT — value proposition + actions */}
            <Box
                sx={{
                    flex: { xs: "1 1 auto", md: "0 1 250px", lg: "0 1 270px" },
                    minWidth: 0,
                    padding: "24px 26px",
                    background: "linear-gradient(135deg, rgba(27,83,238,0.06) 0%, rgba(99,102,241,0.02) 60%, transparent 100%)",
                    display: "flex",
                    flexDirection: "column"
                }}
            >
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                    <Box
                        component="span"
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "9px",
                            background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 3px 8px rgba(99,102,241,0.35)"
                        }}
                    >
                        <HeroIcon name="spark" size={19} color="#fff" />
                    </Box>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: 12,
                            fontWeight: 800,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: "primary.main"
                        }}
                    >
                        Welcome to Product Studio
                    </Typography>
                </Box>

                <Typography
                    component="div"
                    sx={{
                        fontSize: 23,
                        fontWeight: 600,
                        color: "text.primary",
                        letterSpacing: "-0.02em",
                        lineHeight: 1.2
                    }}
                >
                    Turn an idea into shipped software
                </Typography>
                <Box
                    component="ul"
                    sx={{
                        mt: 1.25,
                        mb: 2.25,
                        pl: 2.25,
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: "grey.600",
                        lineHeight: 1.75,
                        listStyleType: "disc",
                        "& > li": { display: "list-item", pl: 0.25 },
                        "& > li::marker": { color: "primary.main" }
                    }}
                >
                    <li>Create your Context Mesh.</li>
                    <li>Define the Product, Features, and Stories.</li>
                    <li>Execute SDD to build the Product and its Work Products.</li>
                </Box>

                <Box sx={{ flex: 1, minHeight: "12px" }} />

                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <Button
                        onClick={onStartProduct}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            height: 38,
                            padding: "0 16px",
                            borderRadius: "9px",
                            backgroundColor: "primary.main",
                            color: "common.white",
                            fontSize: 13.5,
                            fontWeight: 600,
                            textTransform: "none",
                            boxShadow: "0 2px 8px rgba(99,102,241,0.3)",
                            "&:hover": { backgroundColor: "primary.dark" }
                        }}
                    >
                        <HeroIcon name="spark" size={15} color="#fff" />
                        Start Building
                    </Button>
                    {onTakeTour && (
                        <Button
                            onClick={onTakeTour}
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "7px",
                                height: 38,
                                padding: "0 14px",
                                borderRadius: "9px",
                                border: "1px solid",
                                borderColor: "rgba(0, 0, 0, 0.1)",
                                background: "transparent",
                                color: "text.secondary",
                                fontSize: 13,
                                fontWeight: 600,
                                textTransform: "none",
                                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" }
                            }}
                        >
                            <HeroIcon name="play" size={13} />
                            Take the tour
                        </Button>
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                    borderLeft: { xs: "none", md: "1px solid" },
                    borderTop: { xs: "1px solid", md: "none" },
                    borderColor: "divider",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center"
                }}
            >
                <ProductDevelopmentProcess />
            </Box>

            {/* Dismiss */}
            {onDismiss && (
                <Box
                    component="button"
                    onClick={onDismiss}
                    title="Dismiss"
                    aria-label="Dismiss"
                    sx={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        width: 24,
                        height: 24,
                        border: "none",
                        background: "transparent",
                        borderRadius: "6px",
                        cursor: "pointer",
                        color: "grey.500",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" }
                    }}
                >
                    <HeroIcon name="x" size={12} strokeWidth={1.9} />
                </Box>
            )}
        </Box>
    );
};

export default GetStartedHero;
